import { PATH } from '@/constants/path';
import type { AssigneeFilter, WorkflowStatusFilter } from '@/views/tasks/hooks/useTasksFilters';
import { meetingsRangeFor } from '@/views/tasks/utils/meetingsTabParams';

type TasksHrefOptions = {
  assignee?: AssigneeFilter;
  workflowStatus?: WorkflowStatusFilter;
};

/**
 * Build a /tasks deep link using the same query params as useTasksFilters /
 * useListPageUrlSync (assignee, workflowStatus — never legacy aliases like ws).
 */
export function buildTasksHref({
  assignee,
  workflowStatus,
}: TasksHrefOptions = {}): string {
  const params = new URLSearchParams();

  if (workflowStatus && workflowStatus !== 'new') {
    params.set('workflowStatus', workflowStatus);
  }

  // useTasksFilters treats assignee=me as the default and omits it from the URL.
  // Today dashboard links pass the signed-in user's UUID so Actions opens on the
  // same assignee filter the SDR filter dropdown would show for "Me".
  if (assignee && assignee !== 'me') {
    params.set('assignee', assignee);
  }

  const query = params.toString();
  return query ? `${PATH.TASKS}?${query}` : PATH.TASKS;
}

/**
 * One meeting on the Actions meetings tab: the agenda opens on the range that
 * contains it (`range`) with that meeting selected (`meeting`). AE Today's
 * "Open brief" rows link here since the AE Actions hub was retired.
 */
export function buildMeetingsTabHref(meetingId: string, startAt: string | null | undefined): string {
  const params = new URLSearchParams();
  params.set('workflowStatus', 'meetings');
  params.set('meeting', meetingId);
  const range = meetingsRangeFor(startAt);
  if (range) params.set('range', range);
  return `${PATH.TASKS}?${params.toString()}`;
}

/**
 * One Action on /tasks: the signal it belongs to, and optionally the person to
 * open within it. `signalId` is the parameter Tasks scopes the list by
 * (`useActionScope`); `persona` only picks the row once that list is loaded, so
 * it is never sent without the signal.
 */
export function buildActionHref(signalId: string, personaId?: string | null): string {
  const params = new URLSearchParams();
  params.set('signalId', signalId);
  if (personaId) params.set('persona', personaId);
  return `${PATH.TASKS}?${params.toString()}`;
}

/**
 * Link a Today campaign row to that campaign's numbers.
 *
 * It opened `/workflows/{id}`, which is the flow builder: a rep clicking a row of sent /
 * opened / replied counts landed on the canvas that defines the sequence, with no numbers
 * on it (Sunder, 2026-10-21: "clicking the campaign should show me the stats"). Activity →
 * Performance is where the per-campaign numbers live, and it already takes a campaign
 * scope in the URL.
 *
 * The channel comes from the row's own volume, because Activity's tabs are per-channel:
 * audience 7526 is 138 LinkedIn touches to 54 emails, and landing it on the email tab
 * shows a fraction of what the row counted.
 */
export function buildCampaignHref(audienceId: number, channel: 'email' | 'linkedin' = 'email'): string {
  return PATH.CAMPAIGN_ACTIVITY(audienceId, channel);
}
